import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Icon, PasswordField, TextField, toast } from '@/design-system'
import { AppMark } from './AppMark'
import { isAppError } from '@/services/supabase/client'
import { requestPasswordReset, signIn, updatePassword, validateEmail, validatePassword } from './api'
import './auth.css'

export function LoginScreen() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [unconfirmed, setUnconfirmed] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setUnconfirmed(false)

    const emailError = validateEmail(email)
    if (emailError) return setError(emailError)
    if (!password) return setError('Escribe tu contraseña.')

    setBusy(true)
    try {
      await signIn(email, password)
      navigate('/', { replace: true })
    } catch (e) {
      if (isAppError(e) && e.code === 'email_unconfirmed') {
        setUnconfirmed(true)
        setError('Esta cuenta aún no está confirmada. Busca el correo de CatGO y toca el botón.')
      } else {
        setError(isAppError(e) ? e.message : 'No se ha podido entrar.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <header className="auth__head">
        <AppMark />
        <h1 className="auth__mark">
          Cat<span>GO</span>
        </h1>
        <p className="auth__tagline">Recuerda cada gato que conozcas</p>
      </header>

      <div className="auth__middle">
      <form className="auth__form glass" onSubmit={onSubmit} noValidate>

        <TextField
          label="Correo"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@correo.com"
        />

        <PasswordField
          label="Contraseña"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Tu contraseña"
          error={error}
        />

        <div className="auth__actions">
          <Button type="submit" size="lg" block loading={busy}>
            Entrar
          </Button>
          {unconfirmed && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/confirma-correo', { state: { email: email.trim() } })}
            >
              ¿No encuentras el correo? Reenvíalo
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/recuperar')}
          >
            He olvidado la contraseña
          </Button>
        </div>
      </form>

      <p className="auth__stamp">
        <Icon name="lock" size={16} />
        Tu colección es privada desde el primer día
      </p>
      </div>

      <footer className="auth__foot">
        <p className="auth__link">
          ¿Nuevo por aquí? <Link to="/crear-cuenta">Crear cuenta</Link>
        </p>
      </footer>
    </div>
  )
}

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    await requestPasswordReset(email)
    setBusy(false)
    setSent(true)
  }

  return (
    <div className="auth">
      <header className="auth__head">
        <AppMark />
        <h1 className="auth__mark">
          Cat<span>GO</span>
        </h1>
      </header>

      {sent ? (
        <div>
          <h2 className="auth__title">Revisa tu correo</h2>
          <p className="auth__body">
            Si hay una cuenta con esa dirección, encontrarás un enlace para elegir una
            contraseña nueva.
          </p>
        </div>
      ) : (
        <form className="auth__form glass" onSubmit={onSubmit} noValidate>
          <h2 className="auth__title">Recuperar el acceso</h2>
          <p className="auth__body">Te enviamos un enlace para elegir una contraseña nueva.</p>
          <TextField
            label="Correo"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" size="lg" block loading={busy}>
            Enviar enlace
          </Button>
        </form>
      )}

      <footer className="auth__foot">
        <p className="auth__link">
          <Link to="/entrar">Volver a entrar</Link>
        </p>
      </footer>
    </div>
  )
}

export function NewPasswordScreen() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    const problem = validatePassword(password)
    if (problem) return setError(problem)

    setBusy(true)
    try {
      await updatePassword(password)
      toast.success('Contraseña actualizada')
      navigate('/', { replace: true })
    } catch (e) {
      setError(isAppError(e) ? e.message : 'No se ha podido cambiar.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <header className="auth__head">
        <AppMark />
        <h1 className="auth__mark">
          Cat<span>GO</span>
        </h1>
      </header>
      <form className="auth__form glass" onSubmit={onSubmit} noValidate>
        <h2 className="auth__title">Elige una contraseña nueva</h2>
        <PasswordField
          label="Contraseña"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
          hint="Al menos 8 caracteres, con letras y números."
        />
        <Button type="submit" size="lg" block loading={busy}>
          Guardar contraseña
        </Button>
      </form>
    </div>
  )
}
